/**
 * Light/dark theming.
 *
 * The site follows the operating system by default and only remembers a choice once the
 * visitor makes one — so `localStorage` holding nothing is a meaningful state, not an
 * uninitialised one, and is never written to on load.
 *
 * daisyUI does the actual work. Its generated CSS carries the dark palette twice:
 *
 * ```css
 * @media (prefers-color-scheme: dark) { :root:not([data-theme]) { … } }
 * [data-theme='dark'] { … }
 * ```
 *
 * The `:not([data-theme])` guard is what makes this a two-line feature rather than a
 * per-token override: with no attribute the system preference applies, and setting the
 * attribute wins in *both* directions — a visitor on a dark OS can force light, which a
 * `prefers-color-scheme`-only stylesheet cannot express.
 *
 * The attribute is set by an inline script in `templates/base.html`, which must stay in the
 * `<head>` and stay blocking: this module is loaded from a deferred module script, so
 * anything it did would happen after first paint and the wrong theme would flash. By the
 * time the code here runs the page is already the right colour, and its only job is the
 * toggle.
 */

/** An explicit choice. The absence of one — following the system — is `null`. */
export type Theme = 'light' | 'dark';

/**
 * Key under which the choice is stored. Also hard-coded in the inline script in
 * `templates/base.html`; changing it in one place only means the page paints one theme and
 * then the toggle reports another.
 */
export const STORAGE_KEY = 'palinka:theme';

/** Marks the button in the header, and is the hook the toggle's CSS hangs off. */
export const TOGGLE_SELECTOR = '[data-theme-toggle]';

const isTheme = (value: unknown): value is Theme => value === 'light' || value === 'dark';

/**
 * The stored choice, or `null` for "follow the system".
 *
 * Storage access throws rather than returning null in a browser with cookies blocked, and
 * an unreadable preference is not worth breaking a calculator page over — so every access
 * here degrades to the system default.
 */
export function storedTheme(storage: Storage | undefined = safeStorage()): Theme | null {
  try {
    const value = storage?.getItem(STORAGE_KEY);
    return isTheme(value) ? value : null;
  } catch {
    return null;
  }
}

export function storeTheme(theme: Theme, storage: Storage | undefined = safeStorage()): void {
  try {
    storage?.setItem(STORAGE_KEY, theme);
  } catch {
    // A visitor who cannot persist the choice still gets it for this page.
  }
}

function safeStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/** What the OS asks for. Treated as light where the browser has no opinion. */
export function systemTheme(win: Window = window): Theme {
  return win.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** The theme to paint: the stored choice if there is one, otherwise the system's. */
export function resolveTheme(stored: Theme | null, system: Theme): Theme {
  return stored ?? system;
}

export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  root.dataset.theme = theme;
}

/** The theme currently painted, read back off the element the inline script wrote to. */
export function currentTheme(root: HTMLElement = document.documentElement): Theme {
  return root.dataset.theme === 'dark' ? 'dark' : 'light';
}

/**
 * Wire up every toggle on the page.
 *
 * Also keeps following the system for as long as no choice has been stored, so a visitor
 * whose OS flips to dark at sunset sees the page follow without reloading — and stops
 * following the moment they express a preference of their own.
 */
export function mountThemeToggle(
  root: Document | HTMLElement = document,
  win: Window = window,
): void {
  const buttons = root.querySelectorAll<HTMLElement>(TOGGLE_SELECTOR);
  if (buttons.length === 0) return;

  for (const button of buttons) {
    button.addEventListener('click', () => {
      const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
      applyTheme(next);
      storeTheme(next);
    });
  }

  win.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', (event) => {
    if (storedTheme() === null) applyTheme(event.matches ? 'dark' : 'light');
  });
}
